import { store } from '@/app/store'
import type { SchemaDefinitionField } from '@/features/adp/components/shared/SchemaDefinitionForm'
import { getSourceTablesFromResponse } from '@/features/adp/components/source/sheets/sourceConfiguration.helpers'
import { ingestionService } from '@/features/adp/services/ingestion/ingestion.service'
import { sourceService } from '@/features/adp/services/source/source.service'
import type { CreateSourceResponseItem } from '@/features/adp/services/source/source.types'
import { syncCanvasState } from '@/features/adp/state/canvas/canvasSlice'
import { openOverlay } from '@/features/adp/state/overlaySlice'
import { setPipelineState } from '@/features/adp/state/pipelineSlice'
import { useMutation } from '@tanstack/react-query'
import { BaseDatabaseConfig } from '../services/common/common.types'
import { getAdpSessionNode, mergeAdpSessionNode } from './useAdpSessionStore'
import { useSourceData } from './useSourceData'
import { toast } from 'sonner'
import { pipelineService } from '../services/pipeline/pipeline.service'
import { CreateDataFlowResponse } from '../services/pipeline/pipeline.types'

export type PersistedSourceConfigurationState = {
  mode: 'new-source'
  isConfigured: boolean
  isReadOnly: boolean
  values: Record<string, string>
  schemaFields: SchemaDefinitionField[]
  postTestFields: SchemaDefinitionField[]
  source_name?: string
  submitted_source_payload?: BaseDatabaseConfig & Record<string, unknown>
  tables: string[]
  sheetPaths: string[]
  sheetNames: string[]
}

type FinalizeSourceConfigurationParams = {
  nodeId?: string | null
  sourceInstance?: string | null
  dataSourceType?: string | null
  sourceName?: string
  values: Record<string, string>
  schemaFields: SchemaDefinitionField[]
  postTestFields: SchemaDefinitionField[]
  file?: File | null
  fileDelimiter?: string
}

type FinalizeSourceConfigurationResult = {
  dataFlowResponse: CreateDataFlowResponse;
  createSourceResponse: CreateSourceResponseItem;
  submittedSourcePayload: BaseDatabaseConfig & Record<string, unknown>;
}

const OBJECT_PAYLOAD_KEYS = new Set(['api_headers', 'api_body', 'api_params'])

function toObjectPayload(value: unknown): Record<string, unknown> {
  if (value == null) return {}

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.length === 0) return {}
    try {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
      return {}
    } catch {
      return {}
    }
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }

  return {}
}

export function useFinalizeSourceConfigurationMutation() {
  const { setSourceData } = useSourceData()

  return useMutation<FinalizeSourceConfigurationResult, Error, FinalizeSourceConfigurationParams>({
    mutationFn: async ({
      sourceInstance,
      sourceName,
      values,
      schemaFields,
      postTestFields,
      file,
      fileDelimiter,
    }) => {
      const stateSnapshot = store.getState()
      const dataFlowName = stateSnapshot.pipeline.pipelineName.trim() || 'Untitled_Pipeline'
      const dataFlowDescription = stateSnapshot.pipeline.pipelineDescription?.trim() ?? ''

      const dataFlowResponse = await pipelineService.createDataFlow({
        data_flow_name: dataFlowName,
        session_id: crypto.randomUUID(),
        description: dataFlowDescription,
        purpose: '',
        status: '',
        web_socket_connection_id: '',
        data_flow_type: 'ETL',
        validate_source_name: true,
        source_names: sourceName ? [sourceName] : [],
      })

      const isExcelUpload = !!file

      const sourcePayloadKeys = [...schemaFields, ...postTestFields]
        .map(field => field.key)
        .filter((key): key is string => Boolean(key) && key !== 'file_name')

      const sourcePayload = sourcePayloadKeys.reduce<BaseDatabaseConfig & Record<string, unknown>>(
        (acc, key) => {
          const value = values[key] as unknown
          if (OBJECT_PAYLOAD_KEYS.has(key)) {
            acc[key] = toObjectPayload(value)
            return acc
          }

          acc[key] = typeof value === 'string' ? value.trim() : value
          return acc
        },
        { type: sourceInstance ?? '' }
      )

      if (isExcelUpload) {
        const contentType = ingestionService.contentTypeForSourceFileName(file.name)
        const uploadToastId = toast.loading('File upload in progress')
        let path = ''

        try {
          const presignedUrlResponse = await ingestionService.postFilesPresignedUrl({
            databases: [{ type: sourceInstance ?? '', file_delimiter: fileDelimiter ?? '' }],
            file_name: file.name,
            content_type: contentType,
          })
          path = presignedUrlResponse.path

          await ingestionService.putFileToPresignedUrl(
            presignedUrlResponse.upload_url,
            file,
            contentType
          )
        } finally {
          toast.dismiss(uploadToastId)
        }

        sourcePayload.table_path = path
        sourcePayload.file_delimiter = fileDelimiter ?? ''
      }

      const createSourceResponse = await sourceService.createSource({
        databases: [sourcePayload],
      })

      return { dataFlowResponse, createSourceResponse, submittedSourcePayload: sourcePayload }
    },
    onSuccess: ({ dataFlowResponse, createSourceResponse, submittedSourcePayload }, params) => {
      const { nodeId, values, schemaFields, postTestFields, dataSourceType } = params
      const dispatch = store.dispatch
      const sourceTables = getSourceTablesFromResponse(createSourceResponse)
      const bucketNameFromResponse =
        typeof createSourceResponse.bucket_name === 'string'
          ? createSourceResponse.bucket_name
          : undefined
      const enrichedSourcePayload =
        bucketNameFromResponse && !submittedSourcePayload.bucket_name
          ? { ...submittedSourcePayload, bucket_name: bucketNameFromResponse }
          : submittedSourcePayload
      const resolvedSourceName = [
        createSourceResponse.source_name,
        typeof createSourceResponse.data_source_name === 'string'
          ? createSourceResponse.data_source_name
          : undefined,
        values.source_name,
      ].find(
        (candidate): candidate is string =>
          typeof candidate === 'string' && candidate.trim().length > 0
      )
      if (nodeId) {
        setSourceData(nodeId, {
          roo_id: createSourceResponse.roo_id,
          source_name: resolvedSourceName,
          source_data_source_id: createSourceResponse.source_data_source_id,
        })

        const existingNode = getAdpSessionNode(nodeId) ?? {}
        mergeAdpSessionNode(nodeId, {
          ...existingNode,
          sourceConfigurationState: {
            mode: 'new-source',
            isConfigured: true,
            isReadOnly: true,
            values,
            schemaFields,
            postTestFields,
            source_name: resolvedSourceName,
            submitted_source_payload: enrichedSourcePayload,
            tables: sourceTables.tables,
            sheetPaths: sourceTables.sheetPaths,
            sheetNames: sourceTables.sheetNames,
          } satisfies PersistedSourceConfigurationState,
        })

        const snapshot = store.getState()
        const nextNodes = [...snapshot.canvas.present.nodes]
        const sourceNodeIndex = nextNodes.findIndex(
          node => node.id === nodeId && node.type === 'source'
        )

        if (sourceNodeIndex >= 0) {
          const sourceNode = nextNodes[sourceNodeIndex]
          nextNodes[sourceNodeIndex] = {
            ...sourceNode,
            data: {
              ...sourceNode.data,
              nodeSecondaryTitle: resolvedSourceName,
              mode: 'new-source',
              completed_steps: ['configuration-details'],
            },
          } as typeof sourceNode
        }

        dispatch(
          syncCanvasState({
            nodes: nextNodes,
            edges: snapshot.canvas.present.edges,
          })
        )
      }

      dispatch(
        setPipelineState({
          isDataFlowCreated: true,
          sessionId: dataFlowResponse.session_id,
          dataFlowId: dataFlowResponse.data_flow_id,
        })
      )

      dispatch(
        openOverlay({
          type: 'explorer-dialog',
          data: {
            nodeId: nodeId ?? null,
            data_source_type: dataSourceType ?? null,
          },
        })
      )

      toast.success('Source registered successfully')
    },
  })
}
